import React from 'react';
import { Box, Typography, Divider, Paper, CircularProgress, Chip } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { IntroductionSectionProps } from '../../types/lessonPlan.types';
import StoryDisplay from '../StoryComponents/StoryDisplay';
import ActivityDisplay from '../ActivityComponents/ActivityDisplay';
import StreamingText from '../StreamingComponents/StreamingText';
import StreamingIndicator from '../StreamingComponents/StreamingIndicator';

// Extended props for streaming support
interface ExtendedIntroductionSectionProps extends IntroductionSectionProps {
  streamingStates?: {
    [key: string]: {
      isActive: boolean;
      progress: number;
      wordsPerMinute?: number;
      estimatedCompletion?: number;
    };
  };
  streamingContent?: {
    [key: string]: string;
  };
  translationRegenerating?: boolean; // Shows loading state when translations are being regenerated
}

/**
 * IntroductionSection Component
 * 
 * Displays the complete introduction section with real-time streaming support:
 * 1. Introduction Story (5-7 minutes) - with live streaming
 * 2. Lesson Introduction Activities (10-15 minutes) - with live streaming
 * 
 * Total section duration: ≤20 minutes
 */
const IntroductionSection: React.FC<ExtendedIntroductionSectionProps> = ({
  section,
  isStreaming,
  onRegenerate,
  onEdit,
  genre,
  subject,
  streamingStates = {},
  streamingContent = {},
  translationRegenerating = false
}) => {
  
  const introStoryStreaming = streamingStates['intro-story'];
  const introActivitiesStreaming = streamingStates['intro-activities'];
  const introStoryContent = streamingContent['intro-story'] || '';
  const introActivitiesContent = streamingContent['intro-activities'] || '';
  
  return (
    <Box>
      {/* Section Header - Removed per user request */}
      {/*
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <SchoolIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Introduction Section
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Small group lesson introduction and guided practice (≤20 minutes)
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <BookIcon />
            <Typography variant="body2">
              Introduction Story (5-7 min)
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon />
            <Typography variant="body2">
              Pre-reading Activities (10-15 min)
            </Typography>
          </Box>
        </Box>
      </Paper>
      */}

      {/* Story Section (English) */}
      <Box mb={4}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <BookIcon color="primary" />
          {subject === 'Math' ? 'Concept & Examples (I Do, We Do)' : 'Introduction Passage'}
          <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            (5-7 minutes {subject === 'Math' ? 'instruction' : 'reading'} time)
          </Typography>
        </Typography>
        
        {/* Streaming Indicator for Story */}
        {introStoryStreaming?.isActive && (
          <StreamingIndicator
            isActive={introStoryStreaming.isActive}
            progress={introStoryStreaming.progress}
            contentType="Introduction Passage"
            estimatedTime={introStoryStreaming.estimatedCompletion}
            wordsPerMinute={introStoryStreaming.wordsPerMinute}
          />
        )}
        
        {/* Story Content - Use StoryDisplay for all cases */}
        <StoryDisplay 
          story={section.story}
          isStreaming={isStreaming.story}
          editable
          genre={genre}
          onEdit={(content) => onEdit(content, 'story')}
        />
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Activities Section */}
      <Box>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <AssignmentIcon color="secondary" />
          {subject === 'Math' ? 'Practice Problems (You Do)' : 'Lesson Introduction Activities'}
          <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            (10-15 minutes)
          </Typography>
        </Typography>
        
        {/* Streaming Indicator for Activities */}
        {introActivitiesStreaming?.isActive && (
          <StreamingIndicator
            isActive={introActivitiesStreaming.isActive}
            progress={introActivitiesStreaming.progress}
            contentType="Introduction Activities"
            estimatedTime={introActivitiesStreaming.estimatedCompletion}
            wordsPerMinute={introActivitiesStreaming.wordsPerMinute}
          />
        )}
        
        {/* Activities Content - Show streaming or final content */}
        {introActivitiesStreaming?.isActive && introActivitiesContent ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <StreamingText
              content={introActivitiesContent}
              isStreaming={true}
              showCursor={true}
              animationSpeed={20}
            />
          </Paper>
        ) : isStreaming.activities ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <StreamingText
              content={section.activities || ''}
              isStreaming={true}
              showCursor={true}
              animationSpeed={20}
            />
          </Paper>
        ) : (
          <ActivityDisplay
            content={section.activities}
            isStreaming={false}
            type="introduction"
            onEdit={(content) => onEdit(content, 'activities')}
          />
        )}
      </Box>

      {/* Translated Introduction Section (if available) */}
      {(section.storySpanish || section.activitiesSpanish || translationRegenerating) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            🌐 Translated Content
            {translationRegenerating && <CircularProgress size={20} color="secondary" />}
          </Typography>
          
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2,
              border: '2px solid',
              borderColor: 'secondary.light',
              backgroundColor: 'secondary.50',
              position: 'relative'
            }}
          >
            <Chip 
              label="TRANSLATED VERSION" 
              color="secondary" 
              sx={{ 
                position: 'absolute', 
                top: -10, 
                left: 16, 
                zIndex: 1,
                fontSize: '0.75rem',
                fontWeight: 600
              }} 
            />

            {/* Translated Story Section */}
            {translationRegenerating ? (
              <Box mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={40} color="secondary" />
                <Typography variant="body1" sx={{ ml: 2, color: 'text.secondary' }}>
                  Updating translation...
                </Typography>
              </Box>
            ) : (
              <>
                {section.storySpanish && (
                  <Box mb={4}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <BookIcon color="primary" />
                      Introduction Passage
                      <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                        (5-7 minutes reading time)
                      </Typography>
                    </Typography>
                    
                    <StoryDisplay 
                      story={section.storySpanish}
                      isStreaming={false}
                      editable={false}
                      genre={genre}
                    />
                  </Box>
                )}

                <Divider sx={{ mb: 4 }} />

                {/* Translated Activities Section */}
                {section.activitiesSpanish && (
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <AssignmentIcon color="secondary" />
                      Lesson Introduction Activities
                      <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                        (10-15 minutes)
                      </Typography>
                    </Typography>
                    
                    <ActivityDisplay
                      content={section.activitiesSpanish}
                      isStreaming={false}
                      type="introduction"
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* Footer sections removed per user request */}
    </Box>
  );
};

export default IntroductionSection;
 